import React, { useState, useEffect, useCallback } from 'react';
import { FileCheck, Upload, Users, Filter, Eye, Check, X, AlertTriangle, Download, Clock, Search, Trash2, FileText, ExternalLink, Briefcase, Pencil } from 'lucide-react';
import { gestionDocumentalAPI, documentosEmpresaAPI, empleadosAPI, textoErrorApi } from '../api/api';
import { urlConToken } from '../utils/urlConToken';
import VisorDocumento from '../components/VisorDocumento';
import { DialogoConfirmar } from '../components/empleados/RegistroHorarioEmpleado';

const TIPOS_DOCUMENTO = ['EPIS', 'Fichas_Informativas', 'PRL', 'Reconocimiento_Medico', 'Politicas', 'Contrato', 'Otros'];
const ESTADOS = ['Pendiente', 'Descargado', 'Firmado', 'Validado', 'Rechazado'];

// Documentos de empresa (pack CAE): categorías y estados de validez
const CATEGORIAS_EMPRESA = ['Seguro RC', 'Corriente de pago AEAT', 'Corriente de pago TGSS', 'Prevención (PRL)', 'Registro sanitario', 'Banco', 'Trabajadores (CAE)', 'Empresa', 'Otros'];
// ISO (YYYY-MM-DD) -> DD/MM/YYYY para mostrar
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : iso;
};

const VALIDEZ_CONFIG = {
  caducado: { label: 'CADUCADO', cls: 'bg-red-100 text-red-800 border border-red-300' },
  caduca_pronto: { label: 'Caduca pronto', cls: 'bg-amber-100 text-amber-800 border border-amber-300' },
  vigente: { label: 'Vigente', cls: 'bg-green-100 text-green-800 border border-green-300' },
  sin_caducidad: { label: 'Sin caducidad', cls: 'bg-gray-100 text-gray-600 border border-gray-300' },
};

const ESTADO_CONFIG = {
  Pendiente: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: Clock },
  Descargado: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: Download },
  Firmado: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: FileCheck },
  Validado: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: Check },
  Rechazado: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: X },
};

const errorApi = (e, porDefecto) => textoErrorApi(e?.response?.data?.detail, porDefecto);

const GestionDocumental = ({ embedded = false }) => {
  const [tab, setTab] = useState('seguimiento');
  const [plantillas, setPlantillas] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Filters
  const [filtroEmpleado, setFiltroEmpleado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');

  // Modals
  const [showSubirPlantilla, setShowSubirPlantilla] = useState(false);
  const [showAsignar, setShowAsignar] = useState(false);
  const [showRechazar, setShowRechazar] = useState(null);
  const [notasRechazo, setNotasRechazo] = useState('');

  // Visor de documentos: { titulo, url, driveId }
  const [visor, setVisor] = useState(null);
  // Confirmación de borrado: { tipo: 'plantilla'|'empresa', id, nombre }
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState('');

  // Documentos de empresa
  const [docsEmpresa, setDocsEmpresa] = useState([]);
  const [showSubirDocEmpresa, setShowSubirDocEmpresa] = useState(false);
  const [docEmpresaFile, setDocEmpresaFile] = useState(null);
  const [nuevoDocEmpresa, setNuevoDocEmpresa] = useState({ nombre: '', categoria: 'Seguro RC', descripcion: '', fecha_documento: '', fecha_validez: '', empleado_id: '' });
  const [editDocEmpresa, setEditDocEmpresa] = useState(null);
  const [editDocEmpresaFile, setEditDocEmpresaFile] = useState(null); // reemplazo/subida del archivo al editar
  const [filtroEmpleadoCae, setFiltroEmpleadoCae] = useState(''); // filtro por empleado en Trabajadores (CAE)

  // Form states
  const [nuevaPlantilla, setNuevaPlantilla] = useState({ nombre: '', tipo_documento: 'EPIS', descripcion: '' });
  const [plantillaFile, setPlantillaFile] = useState(null);
  const [asignarPlantillaId, setAsignarPlantillaId] = useState('');
  const [asignarEmpleados, setAsignarEmpleados] = useState([]);
  const [asignarFechaLimite, setAsignarFechaLimite] = useState('');

  const fetchData = useCallback(async () => {
    // Cada bloque por separado: si uno falla, el resto se ve igual
    const resultados = await Promise.allSettled([
      gestionDocumentalAPI.plantillas(),
      gestionDocumentalAPI.asignaciones(),
      empleadosAPI.getAll(),
      gestionDocumentalAPI.estadisticas(),
      documentosEmpresaAPI.listar(),
    ]);
    const [plantRes, asigRes, empRes, statsRes, docsEmpRes] = resultados;
    if (plantRes.status === 'fulfilled') setPlantillas(plantRes.value.data || []);
    if (asigRes.status === 'fulfilled') setAsignaciones(asigRes.value.data || []);
    if (empRes.status === 'fulfilled') setEmpleados(empRes.value.data || []);
    if (statsRes.status === 'fulfilled') setStats(statsRes.value.data || null);
    if (docsEmpRes.status === 'fulfilled') setDocsEmpresa(docsEmpRes.value.data || []);
    resultados
      .filter((r) => r.status === 'rejected')
      .forEach((r) => console.error('Error cargando gestión documental:', r.reason));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMsg = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje(null), 4000);
  };

  // ===== DOCUMENTOS DE EMPRESA =====
  const handleSubirDocEmpresa = async () => {
    if (!nuevoDocEmpresa.nombre) {
      showMsg('error', 'Pon un nombre al documento');
      return;
    }
    setLoading(true);
    try {
      if (docEmpresaFile) {
        const formData = new FormData();
        formData.append('file', docEmpresaFile);
        Object.entries(nuevoDocEmpresa).forEach(([k, v]) => formData.append(k, v || ''));
        await documentosEmpresaAPI.subir(formData);
      } else {
        // Sin archivo: queda registrado como pendiente de solicitar
        await documentosEmpresaAPI.crear(nuevoDocEmpresa);
      }
      showMsg('ok', docEmpresaFile ? 'Documento subido' : 'Registrado como pendiente de solicitar');
      setShowSubirDocEmpresa(false);
      setDocEmpresaFile(null);
      setNuevoDocEmpresa({ nombre: '', categoria: 'Seguro RC', descripcion: '', fecha_documento: '', fecha_validez: '', empleado_id: '' });
      fetchData();
    } catch (e) {
      showMsg('error', errorApi(e, 'Error al guardar el documento'));
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarEdicionDocEmpresa = async () => {
    if (!editDocEmpresa) return;
    setLoading(true);
    try {
      try {
        await documentosEmpresaAPI.actualizar(editDocEmpresa.id, {
          nombre: editDocEmpresa.nombre,
          categoria: editDocEmpresa.categoria,
          descripcion: editDocEmpresa.descripcion || '',
          fecha_documento: editDocEmpresa.fecha_documento || '',
          fecha_validez: editDocEmpresa.fecha_validez || '',
          empleado_id: editDocEmpresa.empleado_id || '',
        });
      } catch (e) {
        showMsg('error', errorApi(e, 'Error al actualizar'));
        return;
      }
      // Si se ha seleccionado un archivo nuevo, subirlo/reemplazarlo
      if (editDocEmpresaFile) {
        const formData = new FormData();
        formData.append('file', editDocEmpresaFile);
        try {
          await documentosEmpresaAPI.subirArchivo(editDocEmpresa.id, formData);
        } catch (e) {
          showMsg('error', errorApi(e, 'Datos guardados, pero el archivo no se pudo subir'));
          fetchData();
          return;
        }
      }
      showMsg('ok', 'Documento actualizado');
      setEditDocEmpresa(null);
      setEditDocEmpresaFile(null);
      fetchData();
    } finally {
      setLoading(false);
    }
  };

  // ===== BORRADO (plantilla o documento de empresa), tras confirmar =====
  const confirmarBorrado = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    setErrorBorrar('');
    try {
      if (aBorrar.tipo === 'plantilla') {
        await gestionDocumentalAPI.eliminarPlantilla(aBorrar.id);
        showMsg('ok', 'Plantilla eliminada');
      } else {
        await documentosEmpresaAPI.eliminar(aBorrar.id);
        showMsg('ok', 'Documento eliminado');
      }
      setABorrar(null);
      fetchData();
    } catch (e) {
      setErrorBorrar(errorApi(e, 'No se pudo eliminar'));
    } finally {
      setBorrando(false);
    }
  };

  // ===== SUBIR PLANTILLA =====
  const handleSubirPlantilla = async () => {
    if (!plantillaFile || !nuevaPlantilla.nombre) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', plantillaFile);
      formData.append('nombre', nuevaPlantilla.nombre);
      formData.append('tipo_documento', nuevaPlantilla.tipo_documento);
      formData.append('descripcion', nuevaPlantilla.descripcion);
      await gestionDocumentalAPI.subirPlantilla(formData);
      showMsg('ok', 'Plantilla subida correctamente');
      setShowSubirPlantilla(false);
      setNuevaPlantilla({ nombre: '', tipo_documento: 'EPIS', descripcion: '' });
      setPlantillaFile(null);
      fetchData();
    } catch (e) {
      showMsg('error', errorApi(e, 'Error al subir la plantilla'));
    } finally {
      setLoading(false);
    }
  };

  // ===== ASIGNAR DOCUMENTO =====
  const handleAsignar = async () => {
    if (!asignarPlantillaId || asignarEmpleados.length === 0) return;
    setLoading(true);
    try {
      const res = await gestionDocumentalAPI.asignar(asignarPlantillaId, asignarEmpleados, asignarFechaLimite);
      showMsg('ok', res.data?.message || 'Documento asignado');
      setShowAsignar(false);
      setAsignarEmpleados([]);
      setAsignarFechaLimite('');
      fetchData();
    } catch (e) {
      showMsg('error', errorApi(e, 'Error al asignar'));
    } finally {
      setLoading(false);
    }
  };

  // ===== VALIDAR =====
  const handleValidar = async (asigId) => {
    try {
      await gestionDocumentalAPI.validar(asigId);
      showMsg('ok', 'Documento validado');
      fetchData();
    } catch (e) {
      showMsg('error', errorApi(e, 'Error al validar'));
    }
  };

  // ===== RECHAZAR =====
  const handleRechazar = async () => {
    if (!showRechazar || !notasRechazo.trim()) return;
    try {
      await gestionDocumentalAPI.rechazar(showRechazar, notasRechazo.trim());
      showMsg('ok', 'Documento rechazado, vuelve a Pendiente');
      setShowRechazar(null);
      setNotasRechazo('');
      fetchData();
    } catch (e) {
      showMsg('error', errorApi(e, 'Error al rechazar'));
    }
  };

  // ===== TOGGLE EMPLEADO EN ASIGNACIÓN =====
  const toggleEmpleadoAsignar = (empId) => {
    setAsignarEmpleados(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  // ===== FILTRO =====
  const asignacionesFiltradas = asignaciones.filter(a => {
    if (filtroEmpleado && a.empleado_id !== filtroEmpleado) return false;
    if (filtroTipo && a.tipo_documento !== filtroTipo) return false;
    if (filtroEstado && a.estado !== filtroEstado) return false;
    if (busqueda) {
      const term = busqueda.toLowerCase();
      if (!a.nombre_documento?.toLowerCase().includes(term) && !a.empleado_nombre?.toLowerCase().includes(term)) return false;
    }
    return true;
  });

  const empleadosActivos = empleados.filter(e => e.activo !== false);

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'} data-testid="gestion-documental-page">
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6'}>
        {/* Header */}
        {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-xl">
              <FileCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestión Documental</h1>
              <p className="text-sm text-gray-500">Asignación, firma y validación de documentos</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSubirPlantilla(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              data-testid="btn-subir-plantilla"
            >
              <Upload className="w-4 h-4" /> Subir Plantilla
            </button>
            <button
              onClick={() => setShowAsignar(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
              data-testid="btn-asignar-doc"
            >
              <Users className="w-4 h-4" /> Asignar Documento
            </button>
          </div>
        </div>
        )}
        {embedded && (
          <div className="flex items-center justify-between mb-4 mt-2">
            <div className="flex gap-2">
              <button
                onClick={() => setShowSubirPlantilla(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              >
                <Upload className="w-4 h-4" /> Subir Plantilla
              </button>
              <button
                onClick={() => setShowAsignar(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
              >
                <Users className="w-4 h-4" /> Asignar Documento
              </button>
            </div>
          </div>
        )}

        {/* Mensaje */}
        {mensaje && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`} data-testid="mensaje-gestion">
            {mensaje.tipo === 'ok' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {mensaje.texto}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Total', value: stats.total, color: 'text-gray-900' },
              { label: 'Pendientes', value: stats.pendientes, color: 'text-yellow-600' },
              { label: 'Descargados', value: stats.descargados, color: 'text-blue-600' },
              { label: 'Firmados', value: stats.firmados, color: 'text-orange-600' },
              { label: 'Validados', value: stats.validados, color: 'text-green-600' },
              { label: 'Rechazados', value: stats.rechazados, color: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border p-4 text-center shadow-sm" data-testid={`stat-${s.label.toLowerCase()}`}>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border mb-6">
          {[
            { id: 'seguimiento', label: 'Seguimiento', icon: Eye },
            { id: 'plantillas', label: 'Plantillas', icon: FileText },
            { id: 'empresa', label: 'Empresa', icon: Briefcase },
            { id: 'trabajadores', label: 'Trabajadores (CAE)', icon: Users },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ===== TAB: SEGUIMIENTO ===== */}
        {tab === 'seguimiento' && (
          <div className="space-y-4">
            {/* Filtros */}
            <div className="bg-white rounded-xl border p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filtros</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                    data-testid="filtro-busqueda"
                  />
                </div>
                <select value={filtroEmpleado} onChange={e => setFiltroEmpleado(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm" data-testid="filtro-empleado">
                  <option value="">Todos los empleados</option>
                  {empleadosActivos.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>
                  ))}
                </select>
                <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm" data-testid="filtro-tipo">
                  <option value="">Todos los tipos</option>
                  {TIPOS_DOCUMENTO.map(t => (
                    <option key={t} value={t}>{t.replace('_', ' ')}</option>
                  ))}
                </select>
                <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm" data-testid="filtro-estado">
                  <option value="">Todos los estados</option>
                  {ESTADOS.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Documento</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Empleado</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Tipo</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Estado</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Fecha Límite</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asignacionesFiltradas.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-12 text-gray-400">
                          <FileCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
                          <p>No hay asignaciones{busqueda || filtroEmpleado || filtroTipo || filtroEstado ? ' con estos filtros' : ''}</p>
                        </td>
                      </tr>
                    ) : (
                      asignacionesFiltradas.map(asig => {
                        const config = ESTADO_CONFIG[asig.estado] || ESTADO_CONFIG.Pendiente;
                        const IconEstado = config.icon;
                        return (
                          <tr key={asig.id} className="border-b hover:bg-gray-50 transition" data-testid={`asignacion-row-${asig.id}`}>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{asig.nombre_documento}</div>
                              {asig.notas_rechazo && (
                                <div className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> {asig.notas_rechazo}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{asig.empleado_nombre}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                                {asig.tipo_documento?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                                <IconEstado className="w-3 h-3" /> {asig.estado}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-xs">
                              {asig.fecha_limite ? new Date(asig.fecha_limite).toLocaleDateString('es-ES') : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                {asig.archivo_original_url && (
                                  <button
                                    type="button"
                                    onClick={() => setVisor({ titulo: asig.nombre_documento, url: asig.archivo_original_url })}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                    title="Ver original"
                                    data-testid={`btn-ver-original-${asig.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>
                                )}
                                {(asig.archivo_firmado_url || asig.drive_file_id) && (
                                  <button
                                    type="button"
                                    onClick={() => setVisor({
                                      titulo: `${asig.nombre_documento} (firmado, ${asig.empleado_nombre})`,
                                      url: asig.archivo_firmado_url || null,
                                      driveId: asig.drive_file_id || null,
                                    })}
                                    className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                                    title="Ver firmado"
                                    data-testid={`btn-ver-firmado-${asig.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                )}
                                {asig.drive_link && (
                                  <a
                                    href={asig.drive_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition"
                                    title="Ver en Google Drive"
                                    data-testid={`btn-drive-${asig.id}`}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                                {asig.estado === 'Firmado' && (
                                  <>
                                    <button
                                      onClick={() => handleValidar(asig.id)}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition"
                                      title="Validar"
                                      data-testid={`btn-validar-${asig.id}`}
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => { setShowRechazar(asig.id); setNotasRechazo(''); }}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                                      title="Rechazar"
                                      data-testid={`btn-rechazar-${asig.id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== TAB: PLANTILLAS ===== */}
        {tab === 'plantillas' && (
          <div className="space-y-4">
            {plantillas.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center shadow-sm">
                <Upload className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No hay plantillas. Sube la primera.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plantillas.map(p => (
                  <div key={p.id} className="bg-white rounded-xl border p-5 shadow-sm hover:shadow-md transition" data-testid={`plantilla-card-${p.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                          <FileText className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{p.nombre}</h3>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                            {p.tipo_documento?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => { setErrorBorrar(''); setABorrar({ tipo: 'plantilla', id: p.id, nombre: p.nombre }); }}
                        title="Eliminar plantilla"
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                        data-testid={`btn-eliminar-plantilla-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {p.descripcion && <p className="text-xs text-gray-500 mb-3">{p.descripcion}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {p.archivo_nombre_original}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setVisor({ titulo: p.nombre, url: p.archivo_url })}
                          className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                          data-testid={`btn-ver-plantilla-${p.id}`}
                        >
                          <Eye className="w-3 h-3" /> Ver
                        </button>
                        <a
                          href={urlConToken(p.archivo_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" /> Descargar
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== TABS: DOCUMENTOS DE EMPRESA / TRABAJADORES (CAE) ===== */}
        {(tab === 'empresa' || tab === 'trabajadores') && (() => {
          const docsTab = docsEmpresa.filter(d => {
            if (tab === 'trabajadores') {
              if (d.categoria !== 'Trabajadores (CAE)') return false;
              if (filtroEmpleadoCae && d.empleado_id !== filtroEmpleadoCae) return false;
              return true;
            }
            return d.categoria !== 'Trabajadores (CAE)';
          });
          return (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-gray-600">
                {tab === 'trabajadores'
                  ? 'Documentación CAE por trabajador: entrega de EPIs, formación PRL, aptitud médica, ITA...'
                  : 'Documentación corporativa para clientes (CAE): plan de prevención, corriente de pago, seguros, registro sanitario...'}
              </p>
              <div className="flex items-center gap-2">
                {tab === 'trabajadores' && (
                  <select
                    value={filtroEmpleadoCae}
                    onChange={e => setFiltroEmpleadoCae(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Todos los empleados</option>
                    {empleados.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre}{e.activo === false ? ' (baja)' : ''}</option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => {
                    setNuevoDocEmpresa(prev => ({
                      ...prev,
                      categoria: tab === 'trabajadores' ? 'Trabajadores (CAE)' : 'Seguro RC',
                      empleado_id: tab === 'trabajadores' ? filtroEmpleadoCae : '',
                    }));
                    setShowSubirDocEmpresa(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition"
                >
                  <Upload className="w-4 h-4" /> Subir documento
                </button>
              </div>
            </div>

            {/* Avisos: caducados, a punto de caducar o sin archivo (por solicitar) */}
            {docsTab.some(d => d.estado_validez === 'caducado' || d.estado_validez === 'caduca_pronto' || !d.archivo_url) && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold mb-1">Requieren acción:</p>
                  {docsTab.filter(d => ['caducado', 'caduca_pronto'].includes(d.estado_validez) || !d.archivo_url).map(d => (
                    <p key={d.id}>
                      {d.nombre} — {!d.archivo_url
                        ? 'falta el documento (por solicitar)'
                        : `${d.estado_validez === 'caducado' ? 'caducó' : 'caduca'} el ${fmtFecha(d.fecha_validez)}`}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {docsTab.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center shadow-sm">
                <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No hay documentos en esta sección. Sube el primero.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="px-4 py-3">Documento</th>
                      <th className="px-4 py-3">{tab === 'trabajadores' ? 'Empleado' : 'Categoría'}</th>
                      <th className="px-4 py-3">Fecha doc.</th>
                      <th className="px-4 py-3">Válido hasta</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {docsTab.map(d => {
                      const sinArchivo = !d.archivo_url;
                      const v = sinArchivo
                        ? { label: 'FALTA · solicitar', cls: 'bg-red-100 text-red-800 border border-red-300' }
                        : (VALIDEZ_CONFIG[d.estado_validez] || VALIDEZ_CONFIG.sin_caducidad);
                      return (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{d.nombre}</div>
                            {d.descripcion && <div className="text-xs text-gray-500">{d.descripcion}</div>}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{tab === 'trabajadores' ? (d.empleado_nombre || '—') : d.categoria}</td>
                          <td className="px-4 py-3 text-gray-600">{fmtFecha(d.fecha_documento)}</td>
                          <td className="px-4 py-3 text-gray-600">{fmtFecha(d.fecha_validez)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${v.cls}`}>{v.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {!sinArchivo && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setVisor({ titulo: d.nombre, url: d.archivo_url })}
                                    title="Vista previa"
                                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <a
                                    href={urlConToken(d.archivo_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Descargar"
                                    className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded transition"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                </>
                              )}
                              <button
                                onClick={() => { setEditDocEmpresa({ ...d }); setEditDocEmpresaFile(null); }}
                                title={sinArchivo ? 'Editar / subir archivo' : 'Editar / reemplazar archivo'}
                                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setErrorBorrar(''); setABorrar({ tipo: 'empresa', id: d.id, nombre: d.nombre }); }}
                                title="Eliminar"
                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-400">
              El estado se calcula con la fecha de validez: rojo = caducado, ámbar = caduca en 30 días o menos.
            </p>
          </div>
          );
        })()}
      </div>

      {/* ===== MODAL: SUBIR DOCUMENTO DE EMPRESA ===== */}
      {showSubirDocEmpresa && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Subir documento de empresa</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={nuevoDocEmpresa.nombre}
                  onChange={e => setNuevoDocEmpresa({ ...nuevoDocEmpresa, nombre: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Ej: Seguro RC 2026-2027 (póliza + recibo)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <select
                  value={nuevoDocEmpresa.categoria}
                  onChange={e => setNuevoDocEmpresa({ ...nuevoDocEmpresa, categoria: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {CATEGORIAS_EMPRESA.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {nuevoDocEmpresa.categoria === 'Trabajadores (CAE)' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
                  <select
                    value={nuevoDocEmpresa.empleado_id || ''}
                    onChange={e => setNuevoDocEmpresa({ ...nuevoDocEmpresa, empleado_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Sin asignar</option>
                    {empleados.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre}{e.activo === false ? ' (baja)' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del documento</label>
                  <input
                    type="date"
                    value={nuevoDocEmpresa.fecha_documento}
                    onChange={e => setNuevoDocEmpresa({ ...nuevoDocEmpresa, fecha_documento: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Válido hasta</label>
                  <input
                    type="date"
                    value={nuevoDocEmpresa.fecha_validez}
                    onChange={e => setNuevoDocEmpresa({ ...nuevoDocEmpresa, fecha_validez: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <input
                  type="text"
                  value={nuevoDocEmpresa.descripcion}
                  onChange={e => setNuevoDocEmpresa({ ...nuevoDocEmpresa, descripcion: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Opcional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo (PDF, Word, imagen, Excel)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx"
                  onChange={e => setDocEmpresaFile(e.target.files[0])}
                  className="w-full text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sin archivo, el documento queda registrado como <b>pendiente de solicitar</b> (badge rojo FALTA).
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => { setShowSubirDocEmpresa(false); setDocEmpresaFile(null); }}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubirDocEmpresa}
                disabled={loading}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {loading ? 'Subiendo...' : 'Subir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: EDITAR DOCUMENTO DE EMPRESA ===== */}
      {editDocEmpresa && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Editar documento</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={editDocEmpresa.nombre || ''}
                  onChange={e => setEditDocEmpresa({ ...editDocEmpresa, nombre: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <select
                  value={editDocEmpresa.categoria || 'Otros'}
                  onChange={e => setEditDocEmpresa({ ...editDocEmpresa, categoria: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {CATEGORIAS_EMPRESA.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {editDocEmpresa.categoria === 'Trabajadores (CAE)' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empleado</label>
                  <select
                    value={editDocEmpresa.empleado_id || ''}
                    onChange={e => setEditDocEmpresa({ ...editDocEmpresa, empleado_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Sin asignar</option>
                    {empleados.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre}{e.activo === false ? ' (baja)' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del documento</label>
                  <input
                    type="date"
                    value={editDocEmpresa.fecha_documento || ''}
                    onChange={e => setEditDocEmpresa({ ...editDocEmpresa, fecha_documento: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Válido hasta</label>
                  <input
                    type="date"
                    value={editDocEmpresa.fecha_validez || ''}
                    onChange={e => setEditDocEmpresa({ ...editDocEmpresa, fecha_validez: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <input
                  type="text"
                  value={editDocEmpresa.descripcion || ''}
                  onChange={e => setEditDocEmpresa({ ...editDocEmpresa, descripcion: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editDocEmpresa.archivo_url ? 'Reemplazar archivo (opcional)' : 'Subir archivo'}
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx"
                  onChange={e => setEditDocEmpresaFile(e.target.files[0])}
                  className="w-full text-sm"
                />
                {editDocEmpresa.archivo_url && (
                  <p className="text-xs text-gray-500 mt-1">
                    Actual: {editDocEmpresa.archivo_nombre_original}. Si seleccionas uno nuevo, sustituye al actual (útil para subir la versión renovada).
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => { setEditDocEmpresa(null); setEditDocEmpresaFile(null); }}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardarEdicionDocEmpresa}
                disabled={loading}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: SUBIR PLANTILLA ===== */}
      {showSubirPlantilla && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" data-testid="modal-subir-plantilla">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Subir Plantilla de Documento</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={nuevaPlantilla.nombre}
                  onChange={e => setNuevaPlantilla({ ...nuevaPlantilla, nombre: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Ej: Entrega EPIS 2025"
                  data-testid="input-nombre-plantilla"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={nuevaPlantilla.tipo_documento}
                  onChange={e => setNuevaPlantilla({ ...nuevaPlantilla, tipo_documento: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="select-tipo-plantilla"
                >
                  {TIPOS_DOCUMENTO.map(t => (
                    <option key={t} value={t}>{t.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={nuevaPlantilla.descripcion}
                  onChange={e => setNuevaPlantilla({ ...nuevaPlantilla, descripcion: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  rows="2"
                  placeholder="Descripción opcional..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo PDF *</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={e => setPlantillaFile(e.target.files[0] || null)}
                  className="w-full border rounded-lg px-3 py-2 text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-indigo-600 file:text-white"
                  data-testid="input-file-plantilla"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowSubirPlantilla(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleSubirPlantilla}
                disabled={!nuevaPlantilla.nombre || !plantillaFile || loading}
                className="flex-1 bg-indigo-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                data-testid="btn-confirmar-subir-plantilla">
                {loading ? 'Subiendo...' : 'Subir Plantilla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: ASIGNAR DOCUMENTO ===== */}
      {showAsignar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="modal-asignar">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Asignar Documento a Empleados</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plantilla *</label>
                <select
                  value={asignarPlantillaId}
                  onChange={e => setAsignarPlantillaId(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="select-plantilla-asignar"
                >
                  <option value="">Seleccionar plantilla...</option>
                  {plantillas.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre} ({p.tipo_documento})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Límite (opcional)</label>
                <input
                  type="date"
                  value={asignarFechaLimite}
                  onChange={e => setAsignarFechaLimite(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Empleados *</label>
                  <button
                    onClick={() => {
                      if (asignarEmpleados.length === empleadosActivos.length) {
                        setAsignarEmpleados([]);
                      } else {
                        setAsignarEmpleados(empleadosActivos.map(e => e.id));
                      }
                    }}
                    className="text-xs text-indigo-600 hover:underline"
                    data-testid="btn-seleccionar-todos"
                  >
                    {asignarEmpleados.length === empleadosActivos.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </button>
                </div>
                <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
                  {empleadosActivos.map(emp => (
                    <label
                      key={emp.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition ${
                        asignarEmpleados.includes(emp.id) ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={asignarEmpleados.includes(emp.id)}
                        onChange={() => toggleEmpleadoAsignar(emp.id)}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <span className="text-sm text-gray-800">{emp.nombre} {emp.apellidos}</span>
                      <span className="text-xs text-gray-400 ml-auto">{emp.rol}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">{asignarEmpleados.length} seleccionado(s)</p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAsignar(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleAsignar}
                disabled={!asignarPlantillaId || asignarEmpleados.length === 0 || loading}
                className="flex-1 bg-green-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                data-testid="btn-confirmar-asignar">
                {loading ? 'Asignando...' : `Asignar a ${asignarEmpleados.length} empleado(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: RECHAZAR ===== */}
      {showRechazar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" data-testid="modal-rechazar">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Rechazar Documento</h3>
            <p className="text-sm text-gray-500 mb-4">El documento volverá a estado "Pendiente" y el empleado verá tu nota.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo del rechazo *</label>
              <textarea
                value={notasRechazo}
                onChange={e => setNotasRechazo(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows="3"
                placeholder="Ej: Foto borrosa, falta firma en página 2..."
                data-testid="input-notas-rechazo"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowRechazar(null); setNotasRechazo(''); }}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleRechazar}
                disabled={!notasRechazo.trim()}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                data-testid="btn-confirmar-rechazar">
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CONFIRMAR BORRADO ===== */}
      <DialogoConfirmar
        abierto={!!aBorrar}
        titulo={aBorrar?.tipo === 'plantilla' ? 'Eliminar plantilla' : 'Eliminar documento de empresa'}
        descripcion={aBorrar
          ? (aBorrar.tipo === 'plantilla'
            ? `Se eliminará la plantilla "${aBorrar.nombre}" y todas sus asignaciones.`
            : `Se eliminará el documento "${aBorrar.nombre}".`)
          : ''}
        textoConfirmar="Eliminar"
        peligro
        ocupado={borrando}
        error={errorBorrar}
        onConfirmar={confirmarBorrado}
        onCancelar={() => { setABorrar(null); setErrorBorrar(''); }}
        testId="dialogo-borrar-documental"
      />

      <VisorDocumento
        abierto={!!visor}
        onCerrar={() => setVisor(null)}
        titulo={visor?.titulo}
        url={visor?.url || undefined}
        driveId={visor?.driveId || undefined}
      />
    </div>
  );
};

export default GestionDocumental;
